import { useState, useEffect, useCallback, useRef } from 'react'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { useApiSessionReady } from '@/hooks/useApiSessionReady'
import { isRequestCanceled } from '@/utils/isRequestCanceled'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  emptyDashboardData,
  type DashboardData,
  type DashboardPeriod,
} from '@/hooks/dashboardDataTypes'
import { getCachedData, setCacheData } from '@/hooks/dashboardDataCache'
import { getDateRange } from '@/hooks/dashboardDataDateRange'

export type {
  DashboardPeriod,
  DashboardData,
  DashboardSummary,
  DashboardSales,
  DashboardConversion,
  DashboardCommissions,
  DashboardAdmin,
  DashboardRetention,
  DashboardOpsHealth,
  DashboardLatestDeals,
  DashboardRecentActivity,
} from '@/hooks/dashboardDataTypes'

interface UseDashboardDataReturn {
  data: DashboardData
  loading: boolean
  error: string | null
  refresh: () => void
  period: DashboardPeriod
  setPeriod: (p: DashboardPeriod) => void
}

export function useDashboardData(
  initialPeriod: DashboardPeriod = 'Este mês',
): UseDashboardDataReturn {
  const [period, setPeriod] = useState<DashboardPeriod>(initialPeriod)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<DashboardData>(emptyDashboardData)

  const sessionReady = useApiSessionReady()
  const abortRef = useRef<AbortController | null>(null)
  const fetchIdRef = useRef(0)

  const fetchDashboard = useCallback(async (options?: { forceRefresh?: boolean; silent?: boolean }) => {
    const forceRefresh = options?.forceRefresh ?? false
    const silent = options?.silent ?? false
    const fetchId = ++fetchIdRef.current

    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller

    const cacheKey = `${period}`
    const cached = getCachedData(cacheKey) as DashboardData | null

    if (cached && !forceRefresh) {
      setData(cached)
      setLoading(false)
      setError(null)
      void fetchDashboard({ forceRefresh: true, silent: true })
      return
    }

    if (!silent) {
      setLoading(true)
      setError(null)
    }

    const { start_date, end_date } = getDateRange(period)
    const isAdmin = useAuthStore.getState().user?.role?.toLowerCase() === 'admin'
    const reqOpts = { signal: controller.signal, params: { start_date, end_date } }

    try {
      const [sumRes, salesRes, convRes, bdrRes, commRes, latestRes, activityRes] =
        await Promise.allSettled([
          api.get('/dashboard/summary', reqOpts),
          api.get('/dashboard/sales', reqOpts),
          api.get('/dashboard/conversion', reqOpts),
          api.get('/dashboard/bdr', reqOpts),
          api.get('/dashboard/commissions', reqOpts),
          api.get('/dashboard/latest-deals', {
            signal: controller.signal,
            params: { start_date, end_date, limit: 10 },
          }),
          api.get('/dashboard/recent-activity', {
            signal: controller.signal,
            params: { start_date, end_date, limit: 20 },
          }),
        ])

      const pick = <T,>(
        res: PromiseSettledResult<{ data: { data?: T } }>,
        fallback: T | null | undefined,
      ): T | null =>
        res.status === 'fulfilled' ? (res.value.data.data ?? null) : (fallback ?? null)

      let admin: DashboardData['admin'] = cached?.admin ?? null
      let retention: DashboardData['retention'] = cached?.retention ?? null
      let opsHealth: DashboardData['opsHealth'] = cached?.opsHealth ?? null

      if (isAdmin) {
        const [adminRes, retRes, opsRes] = await Promise.allSettled([
          api.get('/dashboard/admin', reqOpts),
          api.get('/dashboard/retention', reqOpts),
          api.get('/dashboard/ops-health', { signal: controller.signal }),
        ])
        admin = pick(adminRes, admin)
        retention = pick(retRes, retention)
        opsHealth = pick(opsRes, opsHealth)
      }

      const newData: DashboardData = {
        summary: pick(sumRes, cached?.summary),
        sales: pick(salesRes, cached?.sales),
        conversion: pick(convRes, cached?.conversion),
        bdr: pick(bdrRes, cached?.bdr),
        commissions: pick(commRes, cached?.commissions),
        admin,
        retention,
        opsHealth,
        latestDeals: pick(latestRes, cached?.latestDeals),
        recentActivity: pick(activityRes, cached?.recentActivity),
      }

      if (fetchId !== fetchIdRef.current) return

      setCacheData(cacheKey, newData)
      setData(newData)

      const coreResults = [sumRes, salesRes, convRes, bdrRes, commRes]
      const realFailures = coreResults.filter(
        (r) => r.status === 'rejected' && !isRequestCanceled(r.reason),
      )
      if (!silent && realFailures.length === coreResults.length) {
        const firstReason =
          realFailures[0].status === 'rejected' ? realFailures[0].reason : undefined
        setError(getFriendlyError(firstReason, 'Erro ao carregar dados do dashboard'))
      }
    } catch (err: unknown) {
      if (fetchId !== fetchIdRef.current) return
      if (isRequestCanceled(err)) return
      if (!silent) {
        setError(getFriendlyError(err, 'Erro ao carregar dados do dashboard'))
      }
    } finally {
      if (fetchId === fetchIdRef.current && !silent) {
        setLoading(false)
      }
    }
  }, [period])

  useEffect(() => {
    if (!sessionReady) return
    fetchDashboard()
    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
      }
    }
  }, [fetchDashboard, sessionReady])

  const refresh = useCallback(() => {
    fetchDashboard({ forceRefresh: true })
  }, [fetchDashboard])

  return {
    data,
    loading,
    error,
    refresh,
    period,
    setPeriod,
  }
}
